import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ChevronRight, ChevronDown, ChevronUp, Clock, GraduationCap, Sparkles, X } from 'lucide-react';
import api from '../utils/api';

/**
 * GrammarRecommendations Component
 * 
 * Shows personalized grammar topic recommendations based on:
 * - User's active learning languages
 * - Recently imported text language
 * - Forge Path lesson completion
 * 
 * Usage:
 * <GrammarRecommendations /> - General personalized recommendations
 * <GrammarRecommendations type="after-text" languageCode="es" /> - After text import
 * <GrammarRecommendations type="after-forge" languageCode="fr" lessonNumber={15} /> - After Forge lesson
 */

const GrammarRecommendations = ({ 
  type = 'general', // 'general' | 'after-text' | 'after-forge'
  languageCode = null,
  lessonNumber = null,
  limit = 3,
  showDismiss = true,
  defaultCollapsed = true, // Start collapsed by default
  collapsed,
  onCollapsedChange,
  onDismiss,
  className = ''
}) => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [localCollapsed, setLocalCollapsed] = useState(defaultCollapsed);
  const isCollapsed = collapsed ?? localCollapsed;
  const setIsCollapsed = value => { setLocalCollapsed(value); onCollapsedChange?.(value); };
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicDetails, setTopicDetails] = useState(null);
  const [loadingTopic, setLoadingTopic] = useState(false);

  const languageInfo = {
    en: { name: 'English', flag: 'gb' },
    es: { name: 'Spanish', flag: 'es' },
    fr: { name: 'French', flag: 'fr' },
    de: { name: 'German', flag: 'de' },
    fa: { name: 'Persian', flag: 'ir' },
    ar: { name: 'Arabic', flag: 'sa' },
    ru: { name: 'Russian', flag: 'ru' },
    zh: { name: 'Chinese', flag: 'cn' },
    ja: { name: 'Japanese', flag: 'jp' },
    ko: { name: 'Korean', flag: 'kr' },
    pt: { name: 'Portuguese', flag: 'pt' },
    it: { name: 'Italian', flag: 'it' },
  };

  useEffect(() => {
    fetchRecommendations();
  }, [type, languageCode, lessonNumber]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      let endpoint = '/grammar-library/recommendations';
      let params = new URLSearchParams();
      params.append('limit', limit.toString());
      
      if (type === 'after-text' && languageCode) {
        endpoint = '/grammar-library/recommendations/after-text';
        params.append('language_code', languageCode);
      } else if (type === 'after-forge' && languageCode && lessonNumber) {
        endpoint = '/grammar-library/recommendations/after-forge';
        params.append('language_code', languageCode);
        params.append('lesson_number', lessonNumber.toString());
      }
      
      const response = await api.get(`${endpoint}?${params}`);
      setRecommendations(response.data.recommendations || []);
      setMessage(response.data.message || '');
    } catch (err) {
      console.error('Failed to load grammar recommendations:', err);
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = async (topicId) => {
    // Open in floating window instead of navigating
    setLoadingTopic(true);
    setSelectedTopic(topicId);
    try {
      const response = await api.get(`/grammar-library/topic/${topicId}`);
      setTopicDetails(response.data.topic);
    } catch (err) {
      console.error('Failed to load topic details:', err);
      // Fallback to navigation if API fails
      navigate(`/grammar-library?open=${topicId}`);
    } finally {
      setLoadingTopic(false);
    }
  };

  const closeTopicModal = () => {
    setSelectedTopic(null);
    setTopicDetails(null);
  };

  if (dismissed || (!loading && recommendations.length === 0)) {
    return null;
  }

  const FlagImage = ({ langCode, size = 20 }) => {
    const info = languageInfo[langCode];
    const countryCode = info?.flag || 'un';
    return (
      <img 
        src={`https://flagcdn.com/w40/${countryCode}.png`}
        alt={info?.name || langCode}
        className="rounded-sm object-cover"
        style={{ width: size, height: size * 0.75 }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  };

  return (
    <>
      <div className={`bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl ${className}`} data-testid="grammar-recommendations">
        {/* Header - Always visible, clickable to expand/collapse */}
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-full flex items-center justify-between gap-2 p-3 hover:bg-purple-500/5 rounded-xl transition-colors"
          data-testid="grammar-recommendations-toggle"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 bg-purple-500/20 rounded-lg">
              <GraduationCap className="w-4 h-4 text-purple-400" />
            </div>
            <span className="text-white font-medium text-sm text-left">Grammar Recommendations</span>
            <Sparkles className="w-3 h-3 shrink-0 text-purple-400" />
            {recommendations.length > 0 && (
              <span className="text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">
                {recommendations.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showDismiss && (
              <button 
                onClick={(e) => { e.stopPropagation(); setDismissed(true); onDismiss?.(); }}
                className="text-slate-500 hover:text-slate-300 p-1"
                data-testid="grammar-recommendations-dismiss"
                aria-label="Dismiss grammar recommendations"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {isCollapsed ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>
        
        {/* Collapsible Content */}
        {!isCollapsed && (
          <div className="px-4 pb-4" data-testid="grammar-recommendations-content">
            {/* Message */}
            {message && (
              <p className="text-slate-400 text-xs mb-3">{message}</p>
            )}
            
            {/* Loading state */}
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
              </div>
            ) : (
              /* Recommendations */
              <div className="space-y-2">
                {recommendations.map((rec) => (
                  <button
                    key={rec.id}
                    onClick={() => handleTopicClick(rec.id)}
                    className="w-full flex items-center gap-3 p-2.5 bg-slate-800/50 hover:bg-slate-800 border border-slate-700 hover:border-purple-500/50 rounded-lg transition-all group text-left"
                  >
                    <FlagImage langCode={rec.language_code} size={24} />
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-sm font-medium truncate group-hover:text-purple-400 transition-colors">
                        {rec.title || rec.objectives?.[0]?.slice(0, 50) || `${rec.cefr_level || ''} Topic`}
                      </h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        {rec.cefr_level && (
                          <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                            {rec.cefr_level}
                          </span>
                        )}
                        {rec.study_time_minutes && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {rec.study_time_minutes}m
                          </span>
                        )}
                        {rec.has_exercises && (
                          <span className="text-xs text-purple-400">
                            + Exercises
                          </span>
                        )}
                      </div>
                      {rec.reason && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{rec.reason}</p>
                      )}
                    </div>
                    
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-purple-400 transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
            
            {/* View All Link */}
            <button
              onClick={() => navigate('/grammar-library')}
              className="w-full mt-3 text-center text-xs text-purple-400 hover:text-purple-300 flex items-center justify-center gap-1"
            >
              <BookOpen className="w-3 h-3" />
              Browse all grammar topics
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Floating Topic Modal */}
      {selectedTopic && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeTopicModal}
        >
          <div 
            className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800/50">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-purple-400" />
                <span className="font-medium text-white">Grammar Lesson</span>
              </div>
              <button 
                onClick={closeTopicModal}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(85vh-60px)] p-4">
              {loadingTopic ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
              ) : topicDetails ? (
                <div className="space-y-4">
                  {/* Title and Meta */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {topicDetails.language && (
                        <FlagImage langCode={topicDetails.language} size={24} />
                      )}
                      <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded uppercase">
                        {topicDetails.cefr_level || topicDetails.level}
                      </span>
                      {topicDetails.study_time_minutes && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {topicDetails.study_time_minutes} min
                        </span>
                      )}
                    </div>
                    <h2 className="text-xl font-bold text-white">{topicDetails.title}</h2>
                    {topicDetails.objectives && (
                      <p className="text-slate-400 text-sm mt-2">{topicDetails.objectives}</p>
                    )}
                  </div>
                  
                  {/* Content */}
                  {topicDetails.content && (
                    <div className="prose prose-invert prose-sm max-w-none">
                      <div 
                        className="text-slate-300 leading-relaxed whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ 
                          __html: topicDetails.content.replace(/\n/g, '<br/>') 
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Exercises */}
                  {topicDetails.exercises && topicDetails.exercises.length > 0 && (
                    <div className="border-t border-slate-700 pt-4">
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-purple-400" />
                        Exercises
                      </h3>
                      <div className="space-y-3">
                        {topicDetails.exercises.map((ex, idx) => (
                          <div key={idx} className="bg-slate-800/50 rounded-lg p-3">
                            <p className="text-white text-sm">{ex.question || ex}</p>
                            {ex.answer && (
                              <details className="mt-2">
                                <summary className="text-xs text-purple-400 cursor-pointer hover:text-purple-300">
                                  Show answer
                                </summary>
                                <p className="text-slate-400 text-sm mt-1 pl-2 border-l-2 border-purple-500/30">
                                  {ex.answer}
                                </p>
                              </details>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-8">Failed to load topic details</p>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-700 bg-slate-800/30 flex justify-between items-center">
              <button
                onClick={() => navigate('/grammar-library')}
                className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
              >
                <BookOpen className="w-4 h-4" />
                View all topics
              </button>
              <button
                onClick={closeTopicModal}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GrammarRecommendations;
